import { Tabs } from 'antd'
import React, { FunctionComponent, useMemo, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'

import { PROPERTY_TYPES } from '../../common/drmTypes'
import { PROPERTY_TAB_KEYS as KEY } from '../../common/propertyDetailTypes'
import { RuianBuildingResponse } from '../../common/ruianTypes'
import { usePropertyDetailAuctionCheckQuery } from '../../graphql/generated'
import { useAppSelector } from '../../redux/hooks'
import { DualPageLayout } from '../components/DualPageLayout/DualPageLayout'
import { PropertyDetailAuction } from '../components/PropertyDetailAuction/PropertyDetailAuction'
import { PropertyDetailBuyback } from '../components/PropertyDetailBuyback/PropertyDetailBuyback'
import { PropertyDetailCollapseButton as CollapseButton } from '../components/PropertyDetailCollapseButton/PropertyDetailCollapseButton'
import { PropertyDetailNote } from '../components/PropertyDetailNote/PropertyDetailNote'
import { PropertyDetailOwnership } from '../components/PropertyDetailOwnership/PropertyDetailOwnership'
import { PropertyDetailPageTitle } from '../components/PropertyDetailPageTitle/PropertyDetailPageTitle'
import { PropertyDetailPrice } from '../components/PropertyDetailPrice/PropertyDetailPrice'
import { PropertyDetailPriceEstimate } from '../components/PropertyDetailPriceEstimate/PropertyDetailPriceEstimate'
import { PropertyDetailProceeding } from '../components/PropertyDetailProceeding/PropertyDetailProceeding'
import { PropertyDetailRisy } from '../components/PropertyDetailRisy/PropertyDetailRisy'
import { PropertyDetailSidebar } from '../components/PropertyDetailSidebar/PropertyDetailSidebar'
import { PropertyDetailSubjects } from '../components/PropertyDetailSubjects/PropertyDetailSubjects'
import { PropertyDetailTimeline } from '../components/PropertyDetailTimeline/PropertyDetailTimeline'
import { PropertyDetailWatchdog } from '../components/PropertyDetailWatchdog/PropertyDetailWatchdog'
import { TabLabel } from '../components/TabLabel/TabLabel'
import { TabLabelProceeding } from '../components/TabLabelProceeding/TabLabelProceeding'
import { TabLabelSubject } from '../components/TabLabelSubject/TabLabelSubject'
import { TabItem, tabsProps } from '../utils/tabsHelpers'
import { useCheckClaims } from '../utils/useCheckClaims'
import { useFetchPropertyDetail } from '../utils/useFetchPropertyDetail'
import { useTabKeyState } from '../utils/useTabKeyState'

export const PropertyDetail: FunctionComponent = () => {
  const {
    params: { propertyType, id },
  } = useRouteMatch<{ propertyType: PROPERTY_TYPES; id: string }>()

  useFetchPropertyDetail(id, propertyType)

  const [isOpen, setIsOpen] = useState(true)
  const [activeTabKey, setActiveTabKey] = useTabKeyState<KEY>(KEY.OWNERSHIP)

  const tester = useCheckClaims('ApiVyvojar')
  const tenantId = useAppSelector(state => state.myProfile.tenantId)
  const drmError = useAppSelector(state => state.property.drm.error)
  const ruianError = useAppSelector(state => state.property.ruian.error)
  const ruianFetching = useAppSelector(state => state.property.ruian.fetching)
  const noTimeline = !useAppSelector(state => state.property.timeline.data)
  const notFound = drmError && ruianError
  const notBuilding = propertyType !== PROPERTY_TYPES.BUILDING
  const noProceeding = !useAppSelector(
    state => state.property.timeline.data?.rizeni?.length
  )
  const isWatched = !!useAppSelector(
    state => state.watchdog.property.data?.[id]
  )
  const addressId =
    useAppSelector(state => state.property.ruian?.data as RuianBuildingResponse)
      ?.adresniMista?.[0]?.kod || 0

  const [{ data: auction }] = usePropertyDetailAuctionCheckQuery({
    pause: !tenantId || !id,
    variables: {
      accountId: tenantId,
      propertyId: id,
    },
  })

  const inAuction = useMemo(
    () => !!auction?.allAuctionProperties?.totalCount,
    [auction?.allAuctionProperties?.totalCount]
  )

  const tabBarExtraContent = useMemo(
    () => ({
      left: <CollapseButton isOpen={isOpen} setIsOpen={setIsOpen} />,
    }),
    [isOpen]
  )

  const tabItems = useMemo<TabItem<KEY>[]>(
    () => [
      {
        key: KEY.OWNERSHIP,
        label: <TabLabel title="Vlastnická práva" />,
        children: (
          <PropertyDetailOwnership propertyId={id} notFound={notFound} />
        ),
      },
      {
        key: KEY.TIMELINE,
        label: <TabLabel title="Časová osa" />,
        disabled: noTimeline,
        children: <PropertyDetailTimeline />,
      },
      {
        key: KEY.SUBJECT,
        label: <TabLabelSubject addressId={addressId} />,
        disabled: !addressId || notBuilding || noTimeline,
        children: <PropertyDetailSubjects addressId={addressId} />,
      },
      {
        key: KEY.PROCEEDING,
        label: <TabLabelProceeding />,
        disabled: noProceeding,
        children: <PropertyDetailProceeding />,
      },
      {
        key: KEY.PRICE_ESTIMATE,
        label: <TabLabel title="Cenový odhad" />,
        children: <PropertyDetailPriceEstimate propertyId={id} />,
      },
      ...(tester && propertyType === PROPERTY_TYPES.UNIT
        ? [
            {
              key: KEY.PRICE,
              label: <TabLabel title="Cenové údaje" />,
              children: <PropertyDetailPrice />,
            },
            {
              key: KEY.BUYBACK,
              label: <TabLabel title="Kalkulačka" />,
              children: <PropertyDetailBuyback propertyId={id} />,
            },
          ]
        : []),
      ...(tester && propertyType === PROPERTY_TYPES.BUILDING
        ? [
            {
              key: KEY.BUYBACK,
              label: <TabLabel title="Kalkulačka" />,
              children: <PropertyDetailBuyback propertyId={id} />,
            },
          ]
        : []),
      {
        key: KEY.RISY,
        label: <TabLabel loading={ruianFetching} title="Detaily obce" />,
        children: <PropertyDetailRisy />,
      },
      {
        key: KEY.NOTE,
        label: <TabLabel loading={ruianFetching} title="Poznámky" />,
        children: <PropertyDetailNote propertyId={id} />,
      },
      ...(isWatched
        ? [
            {
              key: KEY.WATCHDOG,
              label: <TabLabel title="Hlídač" />,
              children: <PropertyDetailWatchdog propertyId={id} />,
            },
          ]
        : []),
      ...(inAuction
        ? [
            {
              key: KEY.AUCTION,
              label: <TabLabel title="Dražba" />,
              children: <PropertyDetailAuction propertyId={id} />,
            },
          ]
        : []),
    ],
    [
      id,
      notFound,
      noTimeline,
      addressId,
      notBuilding,
      noProceeding,
      tester,
      propertyType,
      ruianFetching,
      isWatched,
      inAuction,
    ]
  )

  return (
    <DualPageLayout
      isContainer
      isOpen={isOpen}
      title={
        <PropertyDetailPageTitle propertyId={id} propertyType={propertyType} />
      }
      sidebarContent={
        <PropertyDetailSidebar propertyId={id} notFound={notFound} />
      }
    >
      <Tabs
        {...tabsProps}
        size="small"
        items={tabItems}
        activeKey={notFound ? KEY.OWNERSHIP : activeTabKey}
        onTabClick={setActiveTabKey}
        tabBarExtraContent={tabBarExtraContent}
      />
    </DualPageLayout>
  )
}

export default PropertyDetail
